'use client';

import type { TFunction } from 'i18next';
import {
  AlertTriangle,
  Bell,
  Building2,
  Download,
  FileText,
  Gavel,
  Search,
  UserCircle,
  Wallet,
} from 'lucide-react';
import { documentLabelFromKey } from '@/lib/document-labels';

/**
 * Minimal structural shape shared by `NotificationItem` and the older
 * `WatchlistNotificationItem` — both feed this describer, so an event's
 * wording can't drift between the bell, the history page and the
 * watchlist's "What's new" section.
 */
export interface DescribableNotification {
  event_type: string;
  payload?: Record<string, unknown> | null;
}

export interface NotificationDescription {
  icon: React.ReactNode;
  label: string;
}

/**
 * Icon + one-line label for a notification row.
 *
 * Icons match the categories used by the email templates, so an event looks
 * the same in the bell and in the inbox: documents for watchlist filings,
 * a wallet for credit movements, a warning triangle for anything critical.
 */
/** A non-empty string from an untrusted payload field, else undefined. */
function text(value: unknown): string | undefined {
  return typeof value === 'string' && value.trim() ? value.trim() : undefined;
}

/** "a · b · c" from the parts that are present. */
function joined(...parts: (string | undefined)[]): string {
  return parts.filter(Boolean).join(' · ');
}

export function describeNotification(
  n: DescribableNotification,
  t: TFunction,
): NotificationDescription {
  const p = (n.payload ?? {}) as Record<string, unknown>;
  switch (n.event_type) {
    // ── watchlist ──────────────────────────────────────────────────────
    case 'new_cd_entry': {
      const num = p.entry_number as number | undefined;
      const date = p.entry_date as string | undefined;
      const datePart = date ? ` (${date})` : '';
      return {
        icon: <FileText className="size-4 shrink-0" />,
        label: `${t('watchlist.event.cdEntry', 'New register entry')} #${num ?? '?'}${datePart}`,
      };
    }
    case 'new_balance_sheet': {
      const year = p.year as number | undefined;
      return {
        icon: <FileText className="size-4 shrink-0" />,
        label: year
          ? `${t('watchlist.event.balanceSheet', 'New balance sheet')} ${year}`
          : t('watchlist.event.balanceSheet', 'New balance sheet'),
      };
    }
    case 'new_gesellschafterliste': {
      const date = p.published_at as string | undefined;
      const datePart = date ? ` (${date})` : '';
      return {
        icon: <UserCircle className="size-4 shrink-0" />,
        label: `${t('watchlist.event.gesellschafterliste', 'New shareholders list')}${datePart}`,
      };
    }

    // ── lists (change alerts on user lists) ────────────────────────────
    case 'list_name_change':
      return {
        icon: <FileText className="size-4 shrink-0" />,
        label: t('lists.event.nameChange', {
          name: (p.new_name as string | undefined) ?? '',
          defaultValue: 'Company renamed to "{{name}}"',
        }),
      };
    case 'list_shareholder_change':
      return {
        icon: <UserCircle className="size-4 shrink-0" />,
        label: t('lists.event.shareholderChange', 'Shareholders changed'),
      };
    case 'list_management_change':
      return {
        icon: <UserCircle className="size-4 shrink-0" />,
        label: t('lists.event.managementChange', 'Management changed'),
      };
    case 'list_revenue_change':
      return {
        icon: <FileText className="size-4 shrink-0" />,
        label: t('lists.event.revenueChange', {
          year: (p.new_year as number | undefined) ?? '',
          defaultValue: 'New revenue figure ({{year}})',
        }),
      };
    case 'list_segment_change':
      return {
        icon: <Bell className="size-4 shrink-0" />,
        label: t('lists.event.segmentChange', {
          list: (p.list_name as string | undefined) ?? '',
          defaultValue: 'Moved to another segment in "{{list}}"',
        }),
      };

    // Facet alerts. Payloads are uniform (`old_<key>` / `new_<key>`), so
    // these read the two values they care about and nothing else.
    case 'list_legal_status_change': {
      const status = p.new_legal_status as string | undefined;
      return {
        icon: <AlertTriangle className="size-4 shrink-0" />,
        label: t(`lists.event.legalStatus.${status ?? 'unknown'}`, {
          defaultValue: t('lists.event.legalStatusChange', {
            status: status ?? '',
            defaultValue: 'Legal status changed to {{status}}',
          }),
        }),
      };
    }
    case 'list_employee_change':
      return {
        icon: <UserCircle className="size-4 shrink-0" />,
        label: t('lists.event.employeeChange', {
          count: (p.new_employee_count as number | undefined) ?? 0,
          defaultValue: 'Headcount now {{count}}',
        }),
      };
    case 'list_profit_change':
      return {
        icon: <FileText className="size-4 shrink-0" />,
        label: t('lists.event.profitChange', {
          year: (p.new_profit_year as number | undefined) ?? '',
          defaultValue: 'New profit figure ({{year}})',
        }),
      };
    case 'list_assets_change':
      return {
        icon: <FileText className="size-4 shrink-0" />,
        label: t('lists.event.assetsChange', {
          year: (p.new_aktiva_year as number | undefined) ?? '',
          defaultValue: 'New balance-sheet total ({{year}})',
        }),
      };
    case 'list_filing_change':
      return {
        icon: <FileText className="size-4 shrink-0" />,
        label: t('lists.event.filingChange', 'New annual accounts published'),
      };
    case 'list_tender_change':
      return {
        icon: <Bell className="size-4 shrink-0" />,
        label: t('lists.event.tenderChange', 'New public tender activity'),
      };
    case 'list_address_change':
      return {
        icon: <Bell className="size-4 shrink-0" />,
        label: t('lists.event.addressChange', {
          city: (p.new_city as string | undefined) ?? '',
          defaultValue: 'Registered seat moved to {{city}}',
        }),
      };
    case 'list_website_change':
      return {
        icon: <Bell className="size-4 shrink-0" />,
        label: t('lists.event.websiteChange', {
          domain: (p.new_domain as string | undefined) ?? '',
          defaultValue: 'Website changed to {{domain}}',
        }),
      };
    case 'list_industry_change':
      return {
        icon: <Bell className="size-4 shrink-0" />,
        label: t('lists.event.industryChange', 'Industry classification changed'),
      };

    // ── feeds (filter-based alerts) ────────────────────────────────────
    // Payloads are display fields chosen by the backend producer; every one
    // is optional here so a renamed or missing field degrades to the bare
    // event label instead of "undefined".
    case 'feed_insolvency': {
      const detail = joined(
        text(p.category) ?? text(p.procedure),
        text(p.city) ?? text(p.region),
      );
      return {
        icon: <AlertTriangle className="size-4 shrink-0" />,
        label: detail
          ? t('alerts.event.feedInsolvencyDetail', { detail })
          : t('alerts.event.feedInsolvency'),
      };
    }
    case 'feed_new_company': {
      const detail = joined(
        text(p.legal_form),
        text(p.city) ?? text(p.sitz),
        text(p.founded_on) ?? text(p.erste_eintragung_datum),
      );
      return {
        icon: <Building2 className="size-4 shrink-0" />,
        label: detail
          ? t('alerts.event.feedNewCompanyDetail', { detail })
          : t('alerts.event.feedNewCompany'),
      };
    }
    case 'feed_tender': {
      const title = text(p.title) ?? text(p.tender_title);
      return {
        icon: <Gavel className="size-4 shrink-0" />,
        label: title
          ? t('alerts.event.feedTenderDetail', { title })
          : t('alerts.event.feedTender'),
      };
    }
    case 'feed_saved_search': {
      const name =
        text(p.search_name) ?? text(p.saved_search_name) ?? text(p.subscription_name);
      return {
        icon: <Search className="size-4 shrink-0" />,
        label: name
          ? t('alerts.event.feedSavedSearchDetail', { name })
          : t('alerts.event.feedSavedSearch'),
      };
    }

    // ── credits ────────────────────────────────────────────────────────
    case 'credits_exhausted':
      return {
        icon: <AlertTriangle className="size-4 shrink-0" />,
        label: t(
          'notifications.event.creditsExhausted',
          'Your credits are used up',
        ),
      };
    case 'credits_low':
      return {
        icon: <Wallet className="size-4 shrink-0" />,
        label: t('notifications.event.creditsLow', {
          n: (p.balance as number | undefined) ?? 0,
          defaultValue: 'Running low — {{n}} credits left',
        }),
      };
    case 'alerts_paused':
      return {
        icon: <AlertTriangle className="size-4 shrink-0" />,
        label: t('alerts.event.alertsPaused'),
      };
    case 'credits_granted':
      return {
        icon: <Wallet className="size-4 shrink-0" />,
        label: t('notifications.event.creditsGranted', {
          n: (p.amount as number | undefined) ?? 0,
          defaultValue: '{{n}} credits added',
        }),
      };

    // ── billing (retired Pro trial; old rows still render) ─────────────
    case 'trial_started': {
      const days = (p.trial_days as number | undefined) ?? 7;
      const credits = (p.credits as number | undefined) ?? 100;
      return {
        icon: <Bell className="size-4 shrink-0" />,
        label: t('notifications.event.trialStarted', {
          days,
          n: credits,
          defaultValue:
            'Your {{days}}-day Pro trial has started — {{n}} credits included',
        }),
      };
    }
    case 'trial_ended':
      return {
        icon: <Bell className="size-4 shrink-0" />,
        label: t(
          'notifications.event.trialEnded',
          'Your Pro trial has ended. Every feature stays available, and your remaining credits stay usable.',
        ),
      };

    // ── downloads ──────────────────────────────────────────────────────
    case 'document_purchased': {
      const key = (p.resource_key as string | undefined) ?? '';
      const credits = p.credits as number | undefined;
      return {
        icon: <Download className="size-4 shrink-0" />,
        label: t('notifications.event.documentPurchased', {
          document:
            documentLabelFromKey(key) ??
            t('downloads.untitled', 'Document'),
          n: credits ?? 0,
          defaultValue: '{{document}} added to your downloads ({{n}} credits)',
        }),
      };
    }

    default:
      return {
        icon: <Bell className="size-4 shrink-0" />,
        label: n.event_type,
      };
  }
}
